import { NODE_TYPE_TO_KIND_MAP } from '../converters/shared/constants/index'
import type { LegacyNodeKind } from '../converters/shared/legacyNodes'
import type {
  SwitchboardFlowData,
  SwitchboardNode,
} from '../converters/shared/reactFlowTypes'

export const FIXED_SOURCE_HANDLES_BY_KIND = new Map<
  LegacyNodeKind,
  readonly string[]
>([
  ['schedule', ['whenOpen', 'whenClosed', 'holiday']],
  ['day_night', ['whenOpen', 'whenClosed']],
  ['queue', ['onTimeout', 'onEscape']],
  ['transfer_rollover', ['noAnswer', 'busy']],
  ['auto_attendant', ['onTimeout', 'onInvalid']],
  ['interactive_webhook', ['onTimeout', 'onInvalid']],
  ['ai_agent_v1', ['onError']],
])

export interface FixedHandleRouteConflict {
  sourceNodeId: string
  sourceNodeType: SwitchboardNode['type']
  sourceKind: LegacyNodeKind
  sourceHandle: string
  routes: Array<{
    edgeId: string
    targetNodeId: string
  }>
}

function shouldIncludeNode(node: SwitchboardNode): boolean {
  if (!node.type || !node.id) {
    return false
  }

  return !(
    node.data.deleted === true || node.data.shouldExcludeFromSave === true
  )
}

function getLegacyKind(node: SwitchboardNode): LegacyNodeKind | undefined {
  if (node.type === 'start') {
    return 'start_node'
  }

  return NODE_TYPE_TO_KIND_MAP[node.type]
}

export function findFixedHandleRouteConflicts(
  flowData: SwitchboardFlowData,
): FixedHandleRouteConflict[] {
  const effectiveNodes = flowData.nodes.filter(shouldIncludeNode)
  const effectiveNodeIds = new Set(effectiveNodes.map((node) => node.id))
  const nodeById = new Map(effectiveNodes.map((node) => [node.id, node]))
  const routesByHandle = new Map<
    string,
    {
      sourceNode: SwitchboardNode
      sourceKind: LegacyNodeKind
      sourceHandle: string
      routes: FixedHandleRouteConflict['routes']
    }
  >()

  flowData.edges.forEach((edge) => {
    if (
      !effectiveNodeIds.has(edge.source) ||
      !effectiveNodeIds.has(edge.target) ||
      !edge.sourceHandle
    ) {
      return
    }

    const sourceNode = nodeById.get(edge.source)
    if (!sourceNode) {
      return
    }

    const sourceKind = getLegacyKind(sourceNode)
    const fixedHandles = sourceKind
      ? FIXED_SOURCE_HANDLES_BY_KIND.get(sourceKind)
      : undefined
    if (!sourceKind || !fixedHandles?.includes(edge.sourceHandle)) {
      return
    }

    const routeKey = `${edge.source}|${edge.sourceHandle}`
    const existing = routesByHandle.get(routeKey)
    const route = { edgeId: edge.id, targetNodeId: edge.target }

    if (existing) {
      existing.routes.push(route)
      return
    }

    routesByHandle.set(routeKey, {
      sourceNode,
      sourceKind,
      sourceHandle: edge.sourceHandle,
      routes: [route],
    })
  })

  return Array.from(routesByHandle.values())
    .filter(({ routes }) => routes.length > 1)
    .map(({ sourceNode, sourceKind, sourceHandle, routes }) => ({
      sourceNodeId: sourceNode.id,
      sourceNodeType: sourceNode.type,
      sourceKind,
      sourceHandle,
      routes,
    }))
}
